import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { OutageMap } from '../components/OutageMap';
import { ReportOutageModal } from '../components/ReportOutageModal';
import { AuthModal } from '../components/AuthModal';
import { LanguageSelector } from '../components/LanguageSelector';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { 
  Zap, 
  Droplets, 
  MapPin, 
  Plus, 
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  Home,
  LogOut,
  User,
  Download,
  List,
  Map,
  Shield,
  ThumbsUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APK_URL = "https://customer-assets.emergentagent.com/job_outrage-reporter-app/artifacts/ae2uttyc_SAFI%20ENERGY%20APP-MVP1-ANDR.apk";

export const Dashboard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language } = useLanguage();
  
  const [outages, setOutages] = useState([]);
  const [stats, setStats] = useState({ electricity_count: 0, water_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [filterType, setFilterType] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [outagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/outages`, { params: { status: 'active' } }),
        axios.get(`${API}/outages/stats`)
      ]);
      setOutages(outagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleReportClick = () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      toast.info(t('report_login_required'));
    } else {
      setReportModalOpen(true);
    }
  };

  const handleOutageCreated = (newOutage) => {
    setOutages(prev => [newOutage, ...prev]);
    setStats(prev => ({
      ...prev,
      [newOutage.type === 'electricity' ? 'electricity_count' : 'water_count']: 
        prev[newOutage.type === 'electricity' ? 'electricity_count' : 'water_count'] + 1,
      total_count: prev.total_count + 1
    }));
  };

  const handleResolveOutage = async (outageId) => {
    try {
      await axios.patch(`${API}/outages/${outageId}/resolve`);
      toast.success(t('dashboard_resolved'));
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleConfirmOutage = async (outageId) => {
    try {
      await axios.post(`${API}/outages/${outageId}/confirm`);
      toast.success('Panne confirmée !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const filteredOutages = outages.filter(o => {
    if (filterType === 'all') return true;
    return o.type === filterType;
  });

  const formatDate = (dateString) => {
    try {
      const locale = language === 'fr' ? fr : enUS;
      return format(new Date(dateString), "d MMM 'à' HH:mm", { locale });
    } catch {
      return dateString;
    }
  };

  const getSeverityBadge = (severity) => {
    const config = {
      low: { label: t('dashboard_severity_low'), className: 'bg-green-500/20 text-green-500 border-green-500/30' },
      medium: { label: t('dashboard_severity_medium'), className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' },
      high: { label: t('dashboard_severity_high'), className: 'bg-red-500/20 text-red-500 border-red-500/30' }
    };
    return config[severity] || config.medium;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-primary">
                  <Zap className="h-5 w-5" strokeWidth={2.5} />
                  <Droplets className="h-5 w-5 text-blue-500" strokeWidth={2.5} />
                </div>
                <span className="font-bold text-xl tracking-tight hidden sm:block">Safi Energy</span>
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{t('dashboard_refresh')}</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(APK_URL, '_blank')}
                className="gap-2"
                data-testid="download-btn"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">APK</span>
              </Button>

              <LanguageSelector variant="ghost" />

              {isAuthenticated && user?.is_admin && (
                <Link to="/admin">
                  <Button variant="outline" size="sm" className="gap-2" data-testid="admin-btn">
                    <Shield className="h-4 w-4" />
                    <span className="hidden sm:inline">Admin</span>
                  </Button>
                </Link>
              )}

              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>{user?.name}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={logout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button 
                  size="sm"
                  onClick={() => setAuthModalOpen(true)}
                  className="bg-primary text-primary-foreground font-semibold"
                  data-testid="login-btn"
                >
                  {t('nav_login')}
                </Button>
              )}

              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="home-btn">
                  <Home className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="stat-card bg-card border-border" data-testid="stat-electricity">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('dashboard_electricity')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-primary mt-1">{stats.electricity_count}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard_active_snel')}</p>
            </CardContent>
          </Card>

          <Card className="stat-card bg-card border-border" data-testid="stat-water">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('dashboard_water')}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-blue-500 mt-1">{stats.water_count}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Droplets className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard_active_regideso')}</p>
            </CardContent>
          </Card>

          <Card className="stat-card bg-card border-border" data-testid="stat-total">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wide">{t('dashboard_total')}</p>
                  <p className="text-2xl sm:text-3xl font-bold mt-1">{stats.total_count}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-muted rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard_reported')}</p>
            </CardContent>
          </Card>

          <Card 
            className="stat-card bg-primary/10 border-primary/30 cursor-pointer hover:bg-primary/20 transition-colors" 
            onClick={handleReportClick}
            data-testid="report-card"
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-primary uppercase tracking-wide">{t('dashboard_report')}</p>
                  <p className="text-lg sm:text-xl font-bold text-primary mt-1">{t('dashboard_new_outage')}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
                </div>
              </div>
              <p className="text-xs text-primary/70 mt-2">{t('dashboard_click_to_report')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Map & List Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <TabsList className="bg-muted">
              <TabsTrigger value="map" className="gap-2" data-testid="map-tab">
                <Map className="h-4 w-4" />
                {t('dashboard_map')}
              </TabsTrigger>
              <TabsTrigger value="list" className="gap-2" data-testid="list-tab">
                <List className="h-4 w-4" />
                {t('dashboard_list')}
              </TabsTrigger>
            </TabsList>

            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('all')}
                data-testid="filter-all"
              >
                {t('dashboard_all')}
              </Button>
              <Button
                variant={filterType === 'electricity' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('electricity')}
                className="gap-2"
                data-testid="filter-electricity"
              >
                <Zap className="h-4 w-4" />
                {t('dashboard_electricity')}
              </Button>
              <Button
                variant={filterType === 'water' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType('water')}
                className="gap-2"
                data-testid="filter-water"
              >
                <Droplets className="h-4 w-4" />
                {t('dashboard_water')}
              </Button>
            </div>
          </div>

          <TabsContent value="map" className="mt-0">
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="h-[500px] lg:h-[600px] w-full">
                  <OutageMap outages={filteredOutages} className="h-full w-full" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="list" className="mt-0">
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg uppercase tracking-wide flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  {t('dashboard_active_outages')} ({filteredOutages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {loading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="spinner" />
                    </div>
                  ) : filteredOutages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mb-4 text-secondary" />
                      <p className="font-medium">{t('dashboard_no_outages')}</p>
                      <p className="text-sm">{t('dashboard_all_working')}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredOutages.map((outage) => {
                        const severityConfig = getSeverityBadge(outage.severity);
                        return (
                          <div 
                            key={outage.id} 
                            className="p-4 hover:bg-muted/50 transition-colors"
                            data-testid={`outage-item-${outage.id}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${
                                  outage.type === 'electricity' 
                                    ? 'bg-primary/10' 
                                    : 'bg-blue-500/10'
                                }`}>
                                  {outage.type === 'electricity' ? (
                                    <Zap className="h-5 w-5 text-primary" />
                                  ) : (
                                    <Droplets className="h-5 w-5 text-blue-500" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold">
                                      {outage.type === 'electricity' ? `${t('dashboard_electricity')} (SNEL)` : `${t('dashboard_water')} (REGIDESO)`}
                                    </span>
                                    <Badge className={severityConfig.className}>
                                      {severityConfig.label}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                                    <MapPin className="h-3 w-3" />
                                    <span>{outage.location_name}</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground line-clamp-2">
                                    {outage.description}
                                  </p>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <User className="h-3 w-3" />
                                      {outage.reporter_name}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <ThumbsUp className="h-3 w-3" />
                                      {outage.confirmations || 0} confirmations
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {formatDate(outage.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {isAuthenticated && outage.reported_by !== user?.id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleConfirmOutage(outage.id)}
                                    className="gap-1"
                                    data-testid={`confirm-btn-${outage.id}`}
                                  >
                                    <ThumbsUp className="h-4 w-4" />
                                    Confirmer
                                  </Button>
                                )}
                                {isAuthenticated && outage.reported_by === user?.id && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleResolveOutage(outage.id)}
                                    className="gap-1 text-secondary border-secondary/30 hover:bg-secondary/10"
                                    data-testid={`resolve-btn-${outage.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                    {t('dashboard_resolved')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Floating Action Button (Mobile) */}
        <Button
          onClick={handleReportClick}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl btn-glow lg:hidden"
          data-testid="fab-report"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </main>

      {/* Modals */}
      <ReportOutageModal 
        open={reportModalOpen} 
        onOpenChange={setReportModalOpen}
        onOutageCreated={handleOutageCreated}
      />
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </div>
  );
};
